// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { NotFoundErrorBoundary } from '@/components/not-found-error-boundary';
import { NoteAddOutlined } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { NoteDialog } from '@/note/components/note-dialog';
import { addProjectNote } from '@/project/api/add-project-note';
import { ProjectNoteList } from '@/project/components/project-note-list';
import { Button, Stack } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/_app/projects/$projectKey/notes')({
  component: ProjectNotesRoute,
});

/**
 * Route component for displaying and managing project notes
 */
function ProjectNotesRoute() {
  const { projectKey } = Route.useParams();

  return (
    <NotFoundErrorBoundary
      entityType="project"
      listPath="/projects"
      listLabel="Go to projects"
    >
      <ProjectNotesContent projectKey={projectKey} />
    </NotFoundErrorBoundary>
  );
}

function ProjectNotesContent(props: { projectKey: string }) {
  const queryClient = useQueryClient();
  const dialog = useDisclosure();

  // Use mutation hook for adding notes
  const addNoteMutation = useMutation({
    mutationFn: (content: string) => addProjectNote(props.projectKey, content),
    onSuccess: () => {
      // Invalidate project query (this will also invalidate project notes)
      queryClient.invalidateQueries({
        queryKey: ['project', props.projectKey],
      });
      // Close the dialog on success
      dialog.close();
    },
    onError: (error) => {
      console.error('Failed to add project note:', error);
      // Dialog remains open on error so user can try again
    },
  });

  return (
    <Stack>
      {/* Note Dialog */}
      <NoteDialog
        isOpen={dialog.isOpen}
        onClose={dialog.close}
        onSubmit={(content) => addNoteMutation.mutate(content)}
        isSubmitting={addNoteMutation.isPending}
        title="Add project note"
        submitButtonText="Add"
      />

      <HardfinTabCardWrapper
        title="Notes"
        actions={
          <Button
            size="small"
            onClick={dialog.open}
            startIcon={<NoteAddOutlined fontSize="small" />}
          >
            Add note
          </Button>
        }
      >
        {/* Note List */}
        <ProjectNoteList
          projectKey={props.projectKey}
          onNotesUpdated={() => {
            // Invalidate project query (this will also invalidate project notes)
            queryClient.invalidateQueries({
              queryKey: ['project', props.projectKey],
            });
          }}
        />
      </HardfinTabCardWrapper>
    </Stack>
  );
}
